import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, PoundSterling, Receipt, FileText, Users, Search,
  Loader2, Plus, CheckCircle, Clock, AlertCircle, Calendar,
  Building2, RefreshCw, Eye, Download, Mail, AlertTriangle, Send
} from "lucide-react";
import { AllHoursIcon } from "@/components/AllHoursLogo";
import { API } from "@/App";

export const RentManagementPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("receipts");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Data
  const [receipts, setReceipts] = useState([]);
  const [statements, setStatements] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [arrears, setArrears] = useState([]);
  const [arrearsStats, setArrearsStats] = useState({ total_in_arrears: 0, total_arrears_amount: 0 });
  
  // Receipt modal
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    tenant_id: "",
    amount: "",
    date_received: new Date().toISOString().split('T')[0],
    payment_method: "bank_transfer",
    payment_reference: "",
    paid_direct_to_landlord: false,
    office_notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Statement modal
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementForm, setStatementForm] = useState({
    landlord_id: "",
    start_date: "",
    end_date: ""
  });
  
  // Tenant account modal
  const [showTenantAccount, setShowTenantAccount] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantAccount, setTenantAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [receiptsRes, statementsRes, tenantsRes, landlordsRes, arrearsRes] = await Promise.all([
        axios.get(`${API}/lettings/receipts`, { headers }),
        axios.get(`${API}/lettings/statements`, { headers }),
        axios.get(`${API}/lettings/tenants?status=active`, { headers }),
        axios.get(`${API}/lettings/landlords`, { headers }),
        axios.get(`${API}/lettings/arrears`, { headers }).catch(() => ({ data: { arrears: [], total_in_arrears: 0, total_arrears_amount: 0 } }))
      ]);

      setReceipts(receiptsRes.data.receipts || []);
      setStatements(statementsRes.data.statements || []);
      setTenants(tenantsRes.data.tenants || []);
      setLandlords(landlordsRes.data.landlords || []);
      setArrears(arrearsRes.data.arrears || []);
      setArrearsStats({
        total_in_arrears: arrearsRes.data.total_in_arrears || 0,
        total_arrears_amount: arrearsRes.data.total_arrears_amount || 0
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const downloadStatementPdf = (statementId) => {
    window.open(`${API}/lettings/statements/${statementId}/pdf?token=${token}`, '_blank');
  };

  const emailStatement = async (statementId) => {
    try {
      await axios.post(
        `${API}/lettings/statements/${statementId}/email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statement emailed to landlord");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to email statement");
    }
  };

  const sendArrearsReminder = async (tenantId, tenantName) => {
    try {
      const response = await axios.post(
        `${API}/lettings/arrears/${tenantId}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reminder sent to ${tenantName} for £${response.data.amount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  const handleCreateReceipt = async () => {
    if (!receiptForm.tenant_id || !receiptForm.amount) {
      toast.error("Please select a tenant and enter amount");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/lettings/receipts`,
        {
          ...receiptForm,
          amount: parseFloat(receiptForm.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Receipt ${response.data.reference} created`);
      setShowReceiptModal(false);
      setReceiptForm({
        tenant_id: "",
        amount: "",
        date_received: new Date().toISOString().split('T')[0],
        payment_method: "bank_transfer",
        payment_reference: "",
        paid_direct_to_landlord: false,
        office_notes: ""
      });
      
      // Auto-allocate the receipt
      if (!receiptForm.paid_direct_to_landlord) {
        await axios.post(
          `${API}/lettings/receipts/${response.data.receipt_id}/auto-allocate`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Receipt auto-allocated to outstanding rent");
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create receipt");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateStatement = async () => {
    if (!statementForm.landlord_id || !statementForm.start_date || !statementForm.end_date) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/lettings/landlords/${statementForm.landlord_id}/statements/generate?start_date=${statementForm.start_date}&end_date=${statementForm.end_date}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Statement ${response.data.reference} generated - Net: £${response.data.summary.net_payable.toFixed(2)}`);
      setShowStatementModal(false);
      setStatementForm({ landlord_id: "", start_date: "", end_date: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate statement");
    } finally {
      setSubmitting(false);
    }
  };

  const viewTenantAccount = async (tenantId) => {
    setAccountLoading(true);
    setShowTenantAccount(true);
    
    try {
      const response = await axios.get(
        `${API}/lettings/tenants/${tenantId}/account`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTenantAccount(response.data);
      setSelectedTenant(response.data.tenant);
    } catch (error) {
      toast.error("Failed to load tenant account");
    } finally {
      setAccountLoading(false);
    }
  };

  const generateDemands = async (tenantId) => {
    try {
      const response = await axios.post(
        `${API}/lettings/tenants/${tenantId}/demands/generate?months=12`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      viewTenantAccount(tenantId); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate demands");
    }
  };

  const settleStatement = async (statementId) => {
    try {
      await axios.put(
        `${API}/lettings/statements/${statementId}/settle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statement marked as settled");
      fetchData();
    } catch (error) {
      toast.error("Failed to settle statement");
    }
  };

  const filteredReceipts = receipts.filter(r =>
    r.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.reference?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredStatements = statements.filter(s =>
    s.landlord_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.reference?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate totals
  const totalReceived = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalOutstanding = statements.filter(s => s.status !== 'settled').reduce((sum, s) => sum + (s.summary?.net_payable || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <AllHoursIcon size={40} />
            <div>
              <p className="text-sm text-muted-foreground">Lettings Management</p>
              <h1 className="text-xl font-semibold font-outfit">Rent & Statements</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowReceiptModal(true)} data-testid="add-receipt-btn">
              <Plus className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
            <Button variant="outline" onClick={() => setShowStatementModal(true)} data-testid="generate-statement-btn">
              <FileText className="w-4 h-4 mr-2" />
              Generate Statement
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <PoundSterling className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">£{totalReceived.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total Received</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{receipts.length}</p>
                  <p className="text-xs text-muted-foreground">Receipts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">£{totalOutstanding.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Due to Landlords</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statements.length}</p>
                  <p className="text-xs text-muted-foreground">Statements</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-red-300" onClick={() => setActiveTab("arrears")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">£{arrearsStats.total_arrears_amount.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{arrearsStats.total_in_arrears} in Arrears</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search receipts, statements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="receipts" data-testid="tab-receipts">
              <Receipt className="w-4 h-4 mr-2" />
              Tenant Receipts ({receipts.length})
            </TabsTrigger>
            <TabsTrigger value="statements" data-testid="tab-statements">
              <FileText className="w-4 h-4 mr-2" />
              Landlord Statements ({statements.length})
            </TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-accounts">
              <Users className="w-4 h-4 mr-2" />
              Tenant Accounts
            </TabsTrigger>
            <TabsTrigger value="arrears" data-testid="tab-arrears" className="text-red-600">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Arrears ({arrearsStats.total_in_arrears})
            </TabsTrigger>
          </TabsList>

          {/* Receipts Tab */}
          <TabsContent value="receipts">
            <Card>
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Date</th>
                      <th>Tenant</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Allocated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReceipts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-muted-foreground">
                          No receipts recorded yet
                        </td>
                      </tr>
                    ) : (
                      filteredReceipts.map((receipt) => (
                        <tr key={receipt.receipt_id}>
                          <td>
                            <span className="font-mono font-medium">{receipt.reference}</span>
                          </td>
                          <td>{new Date(receipt.date_received).toLocaleDateString('en-GB')}</td>
                          <td>{receipt.tenant_name}</td>
                          <td className="font-medium">£{receipt.amount?.toFixed(2)}</td>
                          <td>
                            <Badge variant="outline" className="capitalize">
                              {receipt.payment_method?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td>
                            {receipt.amount_allocated >= receipt.amount ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Fully Allocated
                              </Badge>
                            ) : receipt.amount_allocated > 0 ? (
                              <Badge className="bg-amber-100 text-amber-700">
                                £{receipt.amount_allocated?.toFixed(2)} allocated
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <Clock className="w-3 h-3 mr-1" />
                                Unallocated
                              </Badge>
                            )}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewTenantAccount(receipt.tenant_id)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Statements Tab */}
          <TabsContent value="statements">
            <Card>
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Date</th>
                      <th>Landlord</th>
                      <th>Period</th>
                      <th>Net Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStatements.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-muted-foreground">
                          No statements generated yet
                        </td>
                      </tr>
                    ) : (
                      filteredStatements.map((stmt) => (
                        <tr key={stmt.statement_id}>
                          <td>
                            <span className="font-mono font-medium">{stmt.reference}</span>
                          </td>
                          <td>{new Date(stmt.statement_date).toLocaleDateString('en-GB')}</td>
                          <td>{stmt.landlord_name}</td>
                          <td className="text-sm">
                            {new Date(stmt.period_start).toLocaleDateString('en-GB')} - {new Date(stmt.period_end).toLocaleDateString('en-GB')}
                          </td>
                          <td className="font-medium">£{stmt.summary?.net_payable?.toFixed(2)}</td>
                          <td>
                            {stmt.status === 'settled' ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Settled
                              </Badge>
                            ) : stmt.status === 'sent' ? (
                              <Badge className="bg-blue-100 text-blue-700">
                                Sent
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                Draft
                              </Badge>
                            )}
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                title="Download PDF"
                                onClick={() => downloadStatementPdf(stmt.statement_id)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              {stmt.status === 'draft' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => emailStatement(stmt.statement_id)}
                                  title="Email to Landlord"
                                >
                                  <Mail className="w-4 h-4 text-blue-600" />
                                </Button>
                              )}
                              {stmt.status !== 'settled' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => settleStatement(stmt.statement_id)}
                                  title="Mark Settled"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Tenant Accounts Tab */}
          <TabsContent value="accounts">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tenants.map((tenant) => (
                <Card key={tenant.tenant_id} className="hover:border-blue-300 cursor-pointer" onClick={() => viewTenantAccount(tenant.tenant_id)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold">{tenant.name}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                          {tenant.property?.address}
                        </p>
                        <p className="text-sm mt-2">
                          <span className="text-muted-foreground">Rent:</span>{" "}
                          <span className="font-medium">£{tenant.rent_amount?.toLocaleString()} pcm</span>
                        </p>
                      </div>
                      <Button size="sm" variant="ghost">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Arrears Tab */}
          <TabsContent value="arrears">
            {arrears.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="font-medium text-lg mb-2">No Tenants in Arrears</h3>
                  <p className="text-muted-foreground">All rent payments are up to date!</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Property</th>
                        <th>Overdue Amount</th>
                        <th>Overdue Periods</th>
                        <th>Oldest Due</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arrears.map((a) => (
                        <tr key={a.tenant_id}>
                          <td>
                            <div>
                              <p className="font-medium">{a.tenant_name}</p>
                              <p className="text-xs text-muted-foreground">{a.tenant_email || a.tenant_phone || "No contact"}</p>
                            </div>
                          </td>
                          <td>
                            <p className="text-sm truncate max-w-[200px]" title={a.property_address}>
                              {a.property_address}
                            </p>
                          </td>
                          <td>
                            <span className="font-bold text-red-600">£{a.overdue_amount.toLocaleString()}</span>
                          </td>
                          <td>
                            <Badge variant="destructive">{a.overdue_demands} periods</Badge>
                          </td>
                          <td className="text-sm">
                            {a.oldest_overdue ? new Date(a.oldest_overdue).toLocaleDateString('en-GB') : '-'}
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => viewTenantAccount(a.tenant_id)}
                                title="View Account"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {a.tenant_email && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => sendArrearsReminder(a.tenant_id, a.tenant_name)}
                                  title="Send Reminder"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Rent Payment</DialogTitle>
            <DialogDescription>
              Record a payment received from a tenant
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Tenant *</Label>
              <Select
                value={receiptForm.tenant_id}
                onValueChange={(v) => setReceiptForm({ ...receiptForm, tenant_id: v })}
              >
                <SelectTrigger data-testid="receipt-tenant-select">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id}>
                      {t.name || t.tenant_id?.slice(-6)} {t.property_address ? `- ${t.property_address.slice(0, 30)}` : ''} - £{t.rent_amount || 0} pcm
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={receiptForm.amount}
                  onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="receipt-amount"
                />
              </div>
              <div>
                <Label>Date Received *</Label>
                <Input
                  type="date"
                  value={receiptForm.date_received}
                  onChange={(e) => setReceiptForm({ ...receiptForm, date_received: e.target.value })}
                  data-testid="receipt-date"
                />
              </div>
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select
                value={receiptForm.payment_method}
                onValueChange={(v) => setReceiptForm({ ...receiptForm, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="standing_order">Standing Order</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Reference</Label>
              <Input
                value={receiptForm.payment_reference}
                onChange={(e) => setReceiptForm({ ...receiptForm, payment_reference: e.target.value })}
                placeholder="e.g., Bank reference"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="paid_direct"
                checked={receiptForm.paid_direct_to_landlord}
                onChange={(e) => setReceiptForm({ ...receiptForm, paid_direct_to_landlord: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="paid_direct" className="text-sm font-normal">
                Paid direct to landlord (don't include in statement)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateReceipt} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Statement Modal */}
      <Dialog open={showStatementModal} onOpenChange={setShowStatementModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Landlord Statement</DialogTitle>
            <DialogDescription>
              Create a statement showing rent received, fees, and works deducted
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Landlord *</Label>
              <Select
                value={statementForm.landlord_id}
                onValueChange={(v) => setStatementForm({ ...statementForm, landlord_id: v })}
              >
                <SelectTrigger data-testid="statement-landlord-select">
                  <SelectValue placeholder="Select landlord" />
                </SelectTrigger>
                <SelectContent>
                  {landlords.map((l) => (
                    <SelectItem key={l.landlord_id} value={l.landlord_id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start *</Label>
                <Input
                  type="date"
                  value={statementForm.start_date}
                  onChange={(e) => setStatementForm({ ...statementForm, start_date: e.target.value })}
                  data-testid="statement-start-date"
                />
              </div>
              <div>
                <Label>Period End *</Label>
                <Input
                  type="date"
                  value={statementForm.end_date}
                  onChange={(e) => setStatementForm({ ...statementForm, end_date: e.target.value })}
                  data-testid="statement-end-date"
                />
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium mb-1">Statement will include:</p>
              <ul className="text-muted-foreground text-xs space-y-0.5 ml-4 list-disc">
                <li>Rent received in period</li>
                <li>Management fee deduction (% + VAT)</li>
                <li>Any maintenance works deducted</li>
                <li>Net amount payable to landlord</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatementModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateStatement} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Generate Statement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant Account Modal */}
      <Dialog open={showTenantAccount} onOpenChange={setShowTenantAccount}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tenant Account</DialogTitle>
            {selectedTenant && (
              <DialogDescription>
                {selectedTenant.name} - {tenantAccount?.property?.address}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {accountLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : tenantAccount && (
            <div className="space-y-6 py-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-700">
                    £{tenantAccount.summary?.total_due?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-blue-600">Total Due</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-green-700">
                    £{tenantAccount.summary?.total_received?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-green-600">Received</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${tenantAccount.summary?.balance_outstanding > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                  <p className={`text-lg font-bold ${tenantAccount.summary?.balance_outstanding > 0 ? 'text-red-700' : 'text-slate-700'}`}>
                    £{tenantAccount.summary?.balance_outstanding?.toLocaleString() || 0}
                  </p>
                  <p className={`text-xs ${tenantAccount.summary?.balance_outstanding > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                    Outstanding
                  </p>
                </div>
              </div>

              {/* Demands */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Rent Demands</h4>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => generateDemands(selectedTenant.tenant_id)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Generate 12 Months
                  </Button>
                </div>
                {tenantAccount.demands?.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No rent demands. Click "Generate 12 Months" to create.
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2">Due Date</th>
                          <th className="text-left px-3 py-2">Description</th>
                          <th className="text-right px-3 py-2">Amount</th>
                          <th className="text-right px-3 py-2">Received</th>
                          <th className="text-center px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenantAccount.demands?.slice(0, 12).map((d) => (
                          <tr key={d.demand_id} className="border-t">
                            <td className="px-3 py-2">
                              {new Date(d.due_date).toLocaleDateString('en-GB')}
                            </td>
                            <td className="px-3 py-2">{d.period_description}</td>
                            <td className="px-3 py-2 text-right">£{d.amount?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right">£{d.amount_received?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-center">
                              {d.status === 'paid' ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                              ) : d.status === 'partial' ? (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Partial</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Outstanding</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Recent Receipts */}
              <div>
                <h4 className="font-medium mb-2">Recent Payments</h4>
                {tenantAccount.receipts?.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No payments recorded yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {tenantAccount.receipts?.slice(0, 5).map((r) => (
                      <div key={r.receipt_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                        <div>
                          <span className="font-mono text-sm">{r.reference}</span>
                          <span className="text-muted-foreground text-sm ml-2">
                            {new Date(r.date_received).toLocaleDateString('en-GB')}
                          </span>
                        </div>
                        <span className="font-medium text-green-600">£{r.amount?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTenantAccount(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
